/**
 * @typedef {Object} Price
 * @property {string} amount - The price amount as a string.
 * @property {string} currencyCode - The currency code for the price.
 */

/**
 * @typedef {Object} QuantityPriceBreak
 * @property {number} minimumQuantity - The minimum quantity for this price break.
 * @property {Price} price - The price associated with this quantity.
 */

/**
 * @typedef {Object} QuantityRule
 * @property {number} increment - The quantity increment for ordering.
 * @property {number} minimum - The minimum quantity allowed.
 * @property {number} maximum - The maximum quantity allowed.
 */

/**
 * @typedef {Object} ProductVariant
 * @property {string} id - The unique ID of the product variant.
 * @property {string} title - The title of the product variant (e.g., expiration date).
 * @property {Price} price - The price of the product variant.
 * @property {QuantityPriceBreak[]} quantityPriceBreaks - The list of quantity price breaks.
 * @property {QuantityRule} quantityRule - The rules for quantity increments and limits.
 */

/**
 * @typedef {Object} ProductVolumeData
 * @property {string} id - The unique ID of the product.
 * @property {string} title - The title of the product.
 * @property {string} status - The status of the product (e.g., ACTIVE).
 * @property {ProductVariant[]} variants - The list of product variants.
 */

/**
 * Fetches volume pricing info from the server, returns null if not found
 * @param {string} productId
 * @returns {ProductVolumeData | null}
 */
const getVolumePricing = async (productId) => {
  try {
    const request = await fetch("https://chc.sh/volume-pricing?productId=" + productId, {
      headers: { Accept: "application/json" },
      method: "GET",
    });
    const responseArray = await request.json();

    if (Array.isArray(responseArray)) {
      return responseArray[0] || null;
    } else {
      return null;
    }
  } catch (err) {
    console.error(err);
    return null;
  }
};

/**
 * Converts an ISO 8601 date string to a formatted date
 * @param {string} dateString
 * @returns
 */
const formatDate = (dateString) => {
  const date = new Date(dateString);

  const options = { year: "numeric", month: "short", day: "numeric" };
  return date.toLocaleDateString("en-GB", options);
};

const getWeightUnit = (weight, unitKind) => {
  switch (unitKind) {
    case "g":
      if (weight < 1000) {
        return "g";
      } else {
        return "kg";
      }
    case "ml":
      if (weight < 1000) {
        return "ml";
      } else {
        return "L";
      }
    case "L":
      if (weight < 1000) {
        return "L";
      } else {
        return "kL";
      }
    case "kg":
      if (weight < 1000) {
        return "kg";
      } else {
        return "t";
      }
    case "pc":
      return "pc";
    case "unit":
      return "unit";
    default:
      return "g";
  }
};

/**
 * Converts an amount to a unit string (gr or kg) depending on the value.
 * @param {number} amount
 * @returns {string}
 */
const convertAmountToAmoutWithUnit = (amount, baseWeightUnit) => {
  if (amount < 1000) {
    return `${amount + getWeightUnit(amount, baseWeightUnit)}`;
  } else {
    const amountInKg = (amount / 1000).toFixed(2);
    if (amountInKg.search(".00") !== -1) {
      return `${amountInKg.replace(".00", "") + getWeightUnit(amount, baseWeightUnit)}`;
    } else {
      return `${amountInKg + getWeightUnit(amount, baseWeightUnit)}`;
    }
  }
};

/**
 * Determines if a variant represents a unit product based on its title.
 * @param {ProductVariant} variant - The product variant to check.
 * @returns {boolean} - True if the variant is a unit product, false otherwise.
 */
const isUnitProductFromVariant = (variant) => {
  return variant?.title?.toLowerCase().search(" g") !== -1;
};

/**
 * Gets the unit size of a variant.
 * @param {ProductVariant} variant - The product variant.
 * @param {boolean} isUnitProduct - Whether the variant is a unit product.
 * @returns {number} - The unit size of the variant.
 */
const getVariantUnitSize = (variant, isUnitProduct = false) => {
  if (!isUnitProduct) {
    return 1;
  }

  const weightFromApi = Number(variant?.weight);
  if (weightFromApi > 0) {
    return weightFromApi;
  }

  const title = variant?.title?.toLowerCase() || "";
  const match = title.match(/([0-9]+(?:[.,][0-9]+)?)\s*(kg|g|l|ml)\b/);
  if (!match) {
    return 1;
  }

  let amount = Number(match[1].replace(",", "."));
  const unit = match[2];

  if (!(amount > 0)) {
    return 1;
  }

  if (unit === "kg" || unit === "l") {
    amount = amount * 1000;
  }

  return amount;
};

/**
 * Finds the lowest price from the quantity price breaks across all variants.
 * @param {ProductVariant[]} variants - The list of product variants.
 * @param {number} currencyRate - The conversion rate to the active currency.
 * @returns {number} - The lowest price found.
 */
const findLowestPrice = (variants, currencyRate, isUnitProduct = false) => {
  let lowestPrice = Infinity;

  variants.forEach((variant) => {
    const variantUnitSize = getVariantUnitSize(variant, isUnitProduct);

    variant.quantityPriceBreaks.forEach((breakPoint) => {
      const rawPrice = Number(breakPoint.price.amount) * currencyRate;
      const normalizedPrice = isUnitProduct
        ? rawPrice / variantUnitSize
        : rawPrice;

      if (normalizedPrice < lowestPrice) {
        lowestPrice = normalizedPrice;
      }
    });
  });

  return lowestPrice;
};


/**
 * Gets the minimum quantity for a variant.
 * @param {ProductVariant} variant - The product variant.
 * @param {boolean} isUnitProduct - Whether the variant is a unit product.
 * @returns {number} - The minimum quantity for the variant.
 */
const getVariantMinimum = (variant, isUnitProduct = false) => {
  const minimum = variant?.quantityRule?.minimum || 1;
  if (!isUnitProduct) {
    return minimum;
  }

  const unitSize = getVariantUnitSize(variant, isUnitProduct);
  return minimum * unitSize;
};

/**
 * Gets the starting points for the header based on the reference variant.
 * @param {ProductVariant} referenceVariant - The reference variant.
 * @param {boolean} isUnitProduct - Whether the variant is a unit product.
 * @returns {number[]} - The starting points for the header.
 */
const getHeaderStarts = (referenceVariant, isUnitProduct = false) => {
  const starts = [getVariantMinimum(referenceVariant, isUnitProduct)];

  referenceVariant.quantityPriceBreaks.forEach((breakpoint) => {
    if (isUnitProduct) {
      const unitSize = getVariantUnitSize(referenceVariant, isUnitProduct);
      starts.push(breakpoint.minimumQuantity * unitSize);
    } else {
      starts.push(breakpoint.minimumQuantity);
    }
  });

  return starts;
};

/**
 * Calculates the required offset from the header based on the variant's minimum quantity.
 * @param {number} variantMinimum - The minimum quantity for the variant.
 * @param {number[]} headerStarts - The starting points for the header.
 * @returns {number} - The required offset from the header.
 */
const getRequiredOffsetFromHeader = (variantMinimum, headerStarts) => {
  let requiredOffsetFromHeader = 0;

  headerStarts.forEach((start) => {
    if (variantMinimum > start) {
      requiredOffsetFromHeader += 1;
    }
  });

  return requiredOffsetFromHeader;
};

/**
 * Mapping of currency codes to common locales for the specified markets.
 */
const currencyToLocaleMap = {
  DKK: "da-DK", // Denmark (Danish Krone)
  CZK: "cs-CZ", // Czechia (Czech Koruna)
  EUR: "en-DE", // EU (Euro, commonly used in Germany)
  USD: "en-US", // United States (US Dollar)
  GBP: "en-GB", // United Kingdom (British Pound)
  JPY: "ja-JP", // Japanese Yen
  CAD: "en-CA", // Canadian Dollar
  AUD: "en-AU", // Australian Dollar
  CHF: "de-CH", // Swiss Franc
  SEK: "sv-SE", // Swedish Krona
  NOK: "no-NO", // Norwegian Krone
};

/**
 * Infers the locale based on the currency code.
 * @param {string} currency - The currency code (e.g., 'EUR', 'USD').
 * @returns {string} - The inferred locale.
 */
const getLocaleFromCurrency = (currency) => {
  return currencyToLocaleMap[currency] || "da-DK"; // Default to 'da-DK' if not found
};

/**
 * Formats the currency value based on the customer's active currency and inferred locale.
 * @param {number} amount - The price amount to format.
 * @param {string} currency - The currency code (e.g., 'EUR', 'USD').
 * @returns {string} - The formatted price with the appropriate currency symbol.
 */
const formatCurrency = (amount, currency) => {
  const locale = getLocaleFromCurrency(currency); // Infer locale from currency code
  const formattedCurrency = new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currency,
  }).format(amount);

  // remove the trailing dot from kr. if present, exmaple: 1.000 kr. -> 1.000 kr
  if (formattedCurrency.endsWith(".")) {
    return formattedCurrency.slice(0, -1);
  }

  return formattedCurrency;
};

/**
 * Generates the header bar for the volume pricing grid.
 * @param {number} lowestPrice - The lowest price among the variants.
 * @param {string} currentyUnit - The currency code (e.g., 'USD', 'EUR').
 * @param {string} baseWeightUnit - The base weight unit (e.g., 'g', 'kg').
 * @param {Array} priceBreaks - The array of price breaks.
 * @returns {HTMLTableCellElement} - The table header cell element.
 */
const generateHeaderBar = (lowestPrice, currentyUnit, baseWeightUnit, priceBreaks) => {
  const headerCell = document.createElement("th");
  headerCell.setAttribute("colspan", priceBreaks.length + 2);
  headerCell.id = "volume-pricing-grid-header";
  const formattedLowestPrice = formatCurrency(lowestPrice, currentyUnit) + `/${baseWeightUnit}`;
  headerCell.innerHTML = `
    WHOLESALE PRICES
    <span class="normal-font">from</span>
    <span class="italic-price">${formattedLowestPrice}</span>
  `;

  return headerCell;
};

/**
 * Populates the table headers with the quantity break points.
 * @param {QuantityRule} quantityRule - The quantity rule for the product.
 * @param {QuantityPriceBreak[]} priceBreaks - The quantity price breaks for the product.
 * @returns {HTMLTableRowElement} - The table row element containing the headers.
 */
const generateHeaders = (
  quantityRule,
  priceBreaks,
  baseWeightUnit,
  isUnitProduct = false,
  referenceWeight = 1,
) => {
  const headersRow = document.createElement("tr");
  const multiplier = isUnitProduct ? Number(referenceWeight) || 1 : 1;

  // Spacer
  const blankHeader = document.createElement("th");
  headersRow.appendChild(blankHeader);

  // First breakpoint / minimum
  if (quantityRule.minimum !== 1) {
    const header = document.createElement("th");
    header.innerText = convertAmountToAmoutWithUnit(quantityRule.minimum * multiplier, baseWeightUnit);
    header.innerText += ` - `;

    headersRow.appendChild(header);
  } else {
    const header = document.createElement("th");
    header.innerText = convertAmountToAmoutWithUnit(quantityRule.increment * multiplier, baseWeightUnit);
    header.innerText += ` - `;

    headersRow.appendChild(header);
  }

  // Next breakpoints
  priceBreaks.forEach((breakPoint, index) => {
    const th = document.createElement("th");
    const breakMinimum = Number(breakPoint?.minimumQuantity) || 0;
    th.innerText = `${convertAmountToAmoutWithUnit(breakMinimum * multiplier, baseWeightUnit)}`;
    th.innerText += ` - `;

    // If last entry and a maximum is defined
    if (priceBreaks.length - 1 === index && quantityRule.maximum) {
      th.innerText += `${convertAmountToAmoutWithUnit(Number(quantityRule.maximum) * multiplier, baseWeightUnit)}`;
    }

    headersRow.appendChild(th);
  });

  return headersRow;
};

/**
 * Updates the decorative text based on the variant titles.
 *
 * Sets the text to "Unit Sizes" if the first variant title contains " g", otherwise sets it to "Batch Date".
 * @param {ProductVariant[]} variants - List of product variants.
 */
const updateDecorativeText = (variants) => {
  if (variants.length === 0) {
    return;
  }

  if (variants[0].title === undefined) {
    // Cant do much without a title, should never happen.
    return;
  }
  const firstVariantTitle = variants[0].title.toLowerCase();

  if (firstVariantTitle.search(" g") !== -1) {
    document.getElementById("decorative-text").innerText = "Unit Sizes";
  } else {
    document.getElementById("decorative-text").innerText = "Batch Date";
  }
};

/**
 * Generates table entries for a given variant
 *
 * @param {ProductVariant} variant - variant object
 * @param {number} currencyRate - rate of currency
 * @param {string} currentyUnit - unit of currency
 * @param {string} baseWeightUnit - unit of weight
 * @returns {HTMLTableRowElement[]} table row list
 */
const generateEntries = (variants, currencyRate, currentyUnit, baseWeightUnit) => {
  const entries = [];
  const referenceVariant = variants[0];
  const isUnitProduct = isUnitProductFromVariant(referenceVariant);
  const headerStarts = getHeaderStarts(referenceVariant, isUnitProduct);

  for (const variant of variants) {
    const entry = document.createElement("tr");
    const priceBreaks = variant.quantityPriceBreaks;
    const variantMinimum = getVariantMinimum(variant, isUnitProduct);
    const requiredOffsetFromHeader = getRequiredOffsetFromHeader(
      variantMinimum,
      headerStarts,
    );
    const variantUnitSize = getVariantUnitSize(variant, isUnitProduct);

    const variantIsUnit = variant.title.toLowerCase().search(" g") !== -1;
    // First row - Variant name
    const variantCell = document.createElement("th");
    if (variantIsUnit) {
      variantCell.innerText = variant.title;
    } else {
      variantCell.innerText = formatDate(variant.title);
    }
    
    entry.appendChild(variantCell);
    const totalHeaders = headerStarts.length;

    let usedHeaders = 0;
    // Offset if required
    if (requiredOffsetFromHeader > 0) {
      for (let i = 0; i < requiredOffsetFromHeader; i++) {
        if (usedHeaders >= totalHeaders) {
          break;
        }
        const emptyCell = document.createElement("th");
        emptyCell.innerHTML = "&nbsp;";
        emptyCell.style.backgroundColor = "transparent";
        entry.appendChild(emptyCell);

        usedHeaders++;
      }
    }
    if (usedHeaders < totalHeaders) {
      // First row - Base price
      const basePriceCell = document.createElement("th");
      const basePriceRaw = Number(variant.price.amount) * currencyRate;
      const basePrice = isUnitProduct
        ? basePriceRaw / variantUnitSize
        : basePriceRaw;
      basePriceCell.innerText =
        formatCurrency(basePrice, currentyUnit) + `/${baseWeightUnit}`;
      entry.appendChild(basePriceCell);

      usedHeaders++;
    }


    // Next rows - Quantity price breaks
    priceBreaks.forEach((breakPoint) => {
      if (usedHeaders >= totalHeaders) {
        return;
      }

      const priceCell = document.createElement("th");
      const convertedPriceRaw = Number(breakPoint.price.amount) * currencyRate;
      const convertedPrice = isUnitProduct
        ? convertedPriceRaw / variantUnitSize
        : convertedPriceRaw;
      priceCell.innerText =
        formatCurrency(convertedPrice, currentyUnit) + `/${baseWeightUnit}`;

      entry.appendChild(priceCell);
      usedHeaders++;
    });

    entries.push(entry);
  }

  return entries;
};

/**
 *
 * @param {string} productId
 * @returns void
 */
const generatePriceGrid = async (productId) => {
  const volumePricingData = await getVolumePricing(productId);

  if (!volumePricingData) {
    console.error("Volume pricing data not found");
    return;
  }

  if (
    !Array.isArray(volumePricingData.variants) ||
    volumePricingData.variants.length === 0
  ) {
    console.error("Variant not found on related product, skipping.");
    return;
  }

  const firstVariant = volumePricingData.variants[0];
  const quantityRule = firstVariant?.quantityRule || {
    increment: 1,
    minimum: 1,
    maximum: null,
  };
  const priceBreaks = Array.isArray(firstVariant?.quantityPriceBreaks)
    ? firstVariant.quantityPriceBreaks
    : [];
  const currentyUnit = Shopify.currency.active;
  const currencyRate = Shopify.currency.rate;

  let baseWeightUnit;

  const header = document.getElementById("volume-pricing-grid-header");
  const priceWithUnitText = header?.querySelectorAll("span")?.[1]?.innerText;
  baseWeightUnit = priceWithUnitText ? priceWithUnitText.split("/")[1]?.trim() : null;

  if (!baseWeightUnit) {
    console.error("Base weight unit not found, defaulting to g");
    baseWeightUnit = "g";
  }

  const variants = volumePricingData.variants.filter(
    (variant) => variant.title.toLocaleLowerCase().search("sample") === -1
  );

  if (variants.length === 0) {
    console.error("No non-sample variants found, skipping.");
    return;
  }

  const referenceVariant = variants[0];
  const isUnitProduct = isUnitProductFromVariant(referenceVariant);
  const referenceWeight = getVariantUnitSize(referenceVariant, isUnitProduct);

  if (priceBreaks.length === 0) {
    console.warn("Price break not defined on related product, skipping.");
    return;
  }

  const lowestPrice = findLowestPrice(variants, currencyRate, isUnitProduct);
  const table =
    document.getElementsByClassName("volume-pricing-grid")[0] || null;

  if (!table) {
    console.error("Volume pricing grid not found");
    return;
  }

  updateDecorativeText(variants);
  // Clear any existing rows
  table.innerHTML = "";

  // Show table
  table.classList.remove("hidden");
  document.getElementById("decorative-text")?.classList.remove("hidden");

  const tableBody = document.createElement("tbody");
  const headerRow = document.createElement("tr");

  // Table header: "WHOLESALE PRICES"
  const headerCell = generateHeaderBar(
    lowestPrice,
    currentyUnit,
    baseWeightUnit,
    priceBreaks,
  );

  headerRow.appendChild(headerCell);
  tableBody.appendChild(headerRow);

  // -------------------- HEADERS ----------------------

  const headersRow = generateHeaders(
    quantityRule,
    priceBreaks,
    baseWeightUnit,
    isUnitProduct,
    referenceWeight,
  );
  tableBody.append(headersRow);

  // -------------------- ENTRIES ----------------------

  const entryRows = generateEntries(
    variants,
    currencyRate,
    currentyUnit,
    baseWeightUnit,
  );
  tableBody.append(...entryRows);

  // Append table
  table.appendChild(tableBody);
};
